/**
 * 拾色器 HSV / RGB / Hex 换算（供 AppColorPickerPanel 使用）。
 */

function clampByte(value: number): number {
  return Math.max(0, Math.min(255, Math.round(value)))
}

function clampUnit(value: number): number {
  return Math.max(0, Math.min(1, value))
}

/** 解析 `#rrggbb` 为 RGB 通道。 */
export function parseHexToRgb(hex: string): [number, number, number] {
  const raw = hex.replace('#', '').trim()
  if (!/^[0-9a-f]{6}$/i.test(raw)) return [255, 255, 255]
  return [
    parseInt(raw.slice(0, 2), 16),
    parseInt(raw.slice(2, 4), 16),
    parseInt(raw.slice(4, 6), 16),
  ]
}

/** RGB 通道转 `#rrggbb`。 */
export function rgbChannelsToHex(r: number, g: number, b: number): string {
  const toHex = (channel: number) => clampByte(channel).toString(16).padStart(2, '0')
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

/** RGB 转 HSV，色相 0-360，饱和度/明度 0-1。 */
export function rgbToHsv(r: number, g: number, b: number): [number, number, number] {
  const rn = clampByte(r) / 255
  const gn = clampByte(g) / 255
  const bn = clampByte(b) / 255
  const max = Math.max(rn, gn, bn)
  const min = Math.min(rn, gn, bn)
  const delta = max - min
  let hue = 0
  if (delta > 0) {
    if (max === rn) hue = ((gn - bn) / delta) % 6
    else if (max === gn) hue = (bn - rn) / delta + 2
    else hue = (rn - gn) / delta + 4
    hue *= 60
    if (hue < 0) hue += 360
  }
  const saturation = max === 0 ? 0 : delta / max
  return [hue, saturation, max]
}

/** HSV 转 RGB。 */
export function hsvToRgb(hue: number, saturation: number, value: number): [number, number, number] {
  const h = ((hue % 360) + 360) % 360
  const s = clampUnit(saturation)
  const v = clampUnit(value)
  const chroma = v * s
  const x = chroma * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = v - chroma
  let r = 0
  let g = 0
  let b = 0
  if (h < 60) [r, g, b] = [chroma, x, 0]
  else if (h < 120) [r, g, b] = [x, chroma, 0]
  else if (h < 180) [r, g, b] = [0, chroma, x]
  else if (h < 240) [r, g, b] = [0, x, chroma]
  else if (h < 300) [r, g, b] = [x, 0, chroma]
  else [r, g, b] = [chroma, 0, x]
  return [
    clampByte((r + m) * 255),
    clampByte((g + m) * 255),
    clampByte((b + m) * 255),
  ]
}

/** `#rrggbb` 转 HSV。 */
export function hexToHsv(hex: string): [number, number, number] {
  const [r, g, b] = parseHexToRgb(hex)
  return rgbToHsv(r, g, b)
}

/** HSV 转 `#rrggbb`。 */
export function hsvToHex(hue: number, saturation: number, value: number): string {
  const [r, g, b] = hsvToRgb(hue, saturation, value)
  return rgbChannelsToHex(r, g, b)
}
